import React, { useEffect, useMemo, useState } from "react"
import { useSelector } from "react-redux"

import { GET, POST, PUT, httpOk } from "helpers/api"
import { formatDate, formatMoney } from "helpers/helpers"
import TelegramGroupConnect from "pages/parts/TelegramGroupConnect"
import "./style.css"

const TABS = [
	{ id: "tariffs", label: "Тарифы и услуги", icon: "uil-rocket" },
	{ id: "charges", label: "Начисления", icon: "uil-bill" },
	{ id: "transfer", label: "Переводы", icon: "uil-exchange" },
	{ id: "debts", label: "Задолженности и погашения", icon: "uil-file-minus-alt" },
	{ id: "movements", label: "Все движение", icon: "uil-history" },
	{ id: "telegram", label: "Telegram-группа", icon: "uil-telegram-alt" },
]

function categoryLabel(category) {
	if (category === "debt") return "Задолженность"
	if (category === "repayment") return "Погашение"
	if (category === "transfer") return "Перевод"
	return "Операция"
}

function chargeStatusLabel(status) {
	if (Number(status) === 1) return "Открыто"
	if (Number(status) === 2) return "Частично"
	if (Number(status) === 3) return "Закрыто"
	if (Number(status) === 4) return "Отменено"
	return String(status)
}

function SubscriptionAccount() {
	const account = useSelector(state => state.account)
	const [activeTab, setActiveTab] = useState("tariffs")
	const [data, setData] = useState([])
	const [totalBalance, setTotalBalance] = useState(0)
	const [selectedPosId, setSelectedPosId] = useState(null)
	const [histories, setHistories] = useState([])
	const [transfer, setTransfer] = useState({
		from_pos_id: "",
		to_pos_id: "",
		amount: "",
	})

	function notifyBalanceUpdated() {
		window.dispatchEvent(new CustomEvent("pos-balance-updated"))
	}

	async function getData(showLoader = false) {
		const response = await GET("/subscription-account", {}, { loader: showLoader })
		if (!httpOk(response)) return

		const poses = response.data?.data || []
		setData(poses)
		setTotalBalance(Number(response.data?.total_balance || 0))
		setSelectedPosId(current => {
			if (poses.some(item => Number(item.id) === Number(current))) return current
			if (poses.some(item => Number(item.id) === Number(account.pos_id))) return Number(account.pos_id)
			return poses[0]?.id || null
		})
		setTransfer(current => ({
			...current,
			from_pos_id: poses.some(item => Number(item.id) === Number(current.from_pos_id))
				? current.from_pos_id
				: (poses[0]?.id || ""),
			to_pos_id: poses.some(item => Number(item.id) === Number(current.to_pos_id))
				? current.to_pos_id
				: (poses[1]?.id || ""),
		}))
	}

	async function getHistories(type, showLoader = false, posId = null) {
		const payload = { type }
		if (posId) payload.pos_id = posId
		const response = await GET("/subscription-account/histories", payload, { loader: showLoader })
		if (httpOk(response)) setHistories(response.data?.data || [])
	}

	useEffect(() => {
		getData(true)
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (activeTab === "debts" && account.pos_id) {
			getHistories("debts_repayments", true, account.pos_id)
		}
		if (activeTab === "movements") getHistories("all", true)
	}, [activeTab, account.pos_id]) // eslint-disable-line react-hooks/exhaustive-deps

	const selectedPos = useMemo(
		() => data.find(item => Number(item.id) === Number(selectedPosId)) || null,
		[data, selectedPosId]
	)
	const profilePos = useMemo(
		() => data.find(item => Number(item.id) === Number(account.pos_id)) || null,
		[data, account.pos_id]
	)
	const activePoses = data.filter(item => Number(item.status) === 1).length
	const monthlyTotal = data.reduce((sum, item) => sum + Number(item.monthly_total || 0), 0)

	async function toggleService(service) {
		if (!selectedPos) return
		const response = await PUT("/subscription-account/service", {
			pos_id: selectedPos.id,
			service_id: service.id,
			is_active: !service.is_active,
		}, { loader: true })
		if (httpOk(response)) getData()
	}

	async function transferBalance(event) {
		event.preventDefault()
		const response = await POST("/subscription-account/transfer", {
			from_pos_id: Number(transfer.from_pos_id),
			to_pos_id: Number(transfer.to_pos_id),
			amount: Number(transfer.amount),
		}, { loader: true, notification: true })
		if (httpOk(response)) {
			setSelectedPosId(Number(transfer.to_pos_id))
			setTransfer(current => ({ ...current, amount: "" }))
			await getData()
			notifyBalanceUpdated()
			if (activeTab === "movements") getHistories("all")
			if (activeTab === "debts" && account.pos_id) {
				getHistories("debts_repayments", false, account.pos_id)
			}
		}
	}

	async function activatePos() {
		if (!selectedPos) return
		const response = await POST("/subscription-account/activate", {
			pos_id: selectedPos.id,
		}, { loader: true, notification: true })
		if (httpOk(response)) {
			await getData()
			notifyBalanceUpdated()
			if (activeTab === "movements") getHistories("all")
			if (activeTab === "debts" && account.pos_id) {
				getHistories("debts_repayments", false, account.pos_id)
			}
		}
	}

	function renderHistoryTable(emptyText, options = {}) {
		const { hidePosColumn = false, title = null, subtitle = null } = options
		return (
			<section className="subscription-history-card card">
				<div className="card-body">
					{(title || subtitle) &&
						<div className="subscription-history-heading">
							{title && <h5>{title}</h5>}
							{subtitle && <p className="subscription-muted mb-0">{subtitle}</p>}
						</div>
					}
					{histories.length === 0
						? <div className="subscription-history-empty">{emptyText}</div>
						: <div className="table-responsive">
							<table className="table table-bordered mb-0 subscription-history-table">
								<thead>
									<tr>
										<th>Дата</th>
										{!hidePosColumn && <th>Филиал</th>}
										<th>Тип</th>
										<th>Описание</th>
										<th className="text-end">Сумма</th>
									</tr>
								</thead>
								<tbody>
									{histories.map(item => (
										<tr key={item.id}>
											<td className="text-nowrap">
												{formatDate(item.created_at, "dd.MM.yyyy HH:mm")}
											</td>
											{!hidePosColumn && <td>{item.pos_name}</td>}
											<td>
												<span className={`subscription-history-badge ${item.category}`}>
													{categoryLabel(item.category)}
												</span>
											</td>
											<td>
												<div className="subscription-history-title">{item.title}</div>
												{item.note && item.note !== item.title &&
													<small className="text-muted">{item.note}</small>
												}
											</td>
											<td className={`text-end text-nowrap fw-700 ${item.transaction_type === "minus" ? "text-danger" : "text-success"}`}>
												{item.transaction_type === "minus" ? "-" : "+"}
												{formatMoney(item.amount)}
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					}
				</div>
			</section>
		)
	}

	return (
		<div className="subscription-account-page">
			<div className="subscription-account-heading">
				<div>
					<div className="subscription-account-eyebrow">Mison Billing</div>
					<h2>Тарифы и услуги</h2>
					<p>Управляйте подписками и балансами всех филиалов в одном месте.</p>
				</div>
				<button
					type="button"
					className="btn btn-light subscription-refresh"
					onClick={() => {
						getData(true)
						if (activeTab === "debts" && account.pos_id) {
							getHistories("debts_repayments", true, account.pos_id)
						}
						if (activeTab === "movements") getHistories("all", true)
					}}>
					<i className="uil uil-refresh" />
					Обновить
				</button>
			</div>

			<div className="subscription-summary-grid">
				<div className="subscription-summary-card primary">
					<div className="subscription-summary-icon"><i className="uil uil-wallet" /></div>
					<div>
						<span>Общий баланс</span>
						<strong>{formatMoney(totalBalance)} сум</strong>
					</div>
				</div>
				<div className="subscription-summary-card">
					<div className="subscription-summary-icon green"><i className="uil uil-check-circle" /></div>
					<div>
						<span>Активные филиалы</span>
						<strong>{activePoses} из {data.length}</strong>
					</div>
				</div>
				<div className="subscription-summary-card">
					<div className="subscription-summary-icon violet"><i className="uil uil-calendar-alt" /></div>
					<div>
						<span>В месяц по всем филиалам</span>
						<strong>{formatMoney(monthlyTotal)} сум</strong>
					</div>
				</div>
			</div>

			<div className="subscription-tabs">
				{TABS.map(tab => (
					<button
						type="button"
						key={tab.id}
						className={`subscription-tab ${activeTab === tab.id ? "active" : ""}`}
						onClick={() => setActiveTab(tab.id)}>
						<i className={`uil ${tab.icon}`} />
						{tab.label}
					</button>
				))}
			</div>

			{activeTab === "tariffs" &&
				<div className="subscription-content-grid">
					<aside className="subscription-branches card">
						<div className="card-body">
							<h5>Филиалы</h5>
							<p className="subscription-muted">Выберите филиал для управления</p>
							<div className="subscription-branch-list">
								{data.map(item => (
									<button
										type="button"
										key={item.id}
										className={`subscription-branch-item ${Number(item.id) === Number(selectedPosId) ? "active" : ""}`}
										onClick={() => setSelectedPosId(item.id)}>
										<span className="subscription-branch-avatar">{String(item.name || "Ф").slice(0, 1)}</span>
										<span className="subscription-branch-copy">
											<b>{item.name}</b>
											<small>ID {item.id}</small>
										</span>
										<span className={`subscription-branch-balance ${Number(item.balance) < 0 ? "negative" : ""}`}>
											{formatMoney(item.balance)}
										</span>
									</button>
								))}
							</div>
						</div>
					</aside>

					<main className="subscription-main-column">
						{selectedPos &&
							<section className="subscription-plan-card card">
								<div className="card-body">
									<div className="subscription-plan-header">
										<div>
											<div className="d-flex align-items-center gap-2">
												<h4>{selectedPos.name}</h4>
												<span className={`subscription-status ${Number(selectedPos.status) === 1 ? "active" : "inactive"}`}>
													{Number(selectedPos.status) === 1 ? "Активен" : "Отключен"}
												</span>
											</div>
											<p>Текущий тарифный план и подключенные услуги</p>
										</div>
										<div className="subscription-current-balance">
											<span>Баланс филиала</span>
											<strong className={Number(selectedPos.balance) < 0 ? "text-danger" : ""}>
												{formatMoney(selectedPos.balance)} сум
											</strong>
										</div>
									</div>

									<div className="subscription-tariff-box">
										<div className="subscription-tariff-name">
											<span className="subscription-tariff-icon"><i className="uil uil-rocket" /></span>
											<div>
												<small>Основной тариф</small>
												<b>Mison POS</b>
											</div>
										</div>
										<div className="subscription-tariff-price">
											<strong>{formatMoney(selectedPos.tariff)} сум</strong>
											<span>/ месяц</span>
										</div>
									</div>

									<div className="subscription-services-heading">
										<div>
											<h5>Дополнительные услуги</h5>
											<p>Включенные услуги попадут в следующее списание или в текущую активацию.</p>
										</div>
									</div>

									<div className="subscription-services-list">
										{selectedPos.services.map(service => (
											<div className="subscription-service-row" key={service.id}>
												<div className="subscription-service-info">
													<span className="subscription-service-icon">
														<i className={service.code === "fiscalization" ? "uil uil-receipt" : "uil uil-apps"} />
													</span>
													<div>
														<b>{service.name}</b>
														{service.has_paid_access
															? <small>Оплачено — доступно на кассе</small>
															: <small className="text-danger">
																{service.is_active
																	? (service.is_charged_this_month
																		? "Отключено на кассе — начислено, но не оплачено"
																		: "Отключено на кассе — не оплачено")
																	: "Услуга выключена"}
															</small>
														}
													</div>
												</div>
												<div className="subscription-service-actions">
													<strong>{formatMoney(service.price)} сум</strong>
													<label className="subscription-switch">
														<input
															type="checkbox"
															checked={service.is_active}
															onChange={() => toggleService(service)}
														/>
														<span />
													</label>
												</div>
											</div>
										))}
									</div>

									<div className="subscription-plan-footer">
										<div>
											<span>К оплате сейчас</span>
											<strong>{formatMoney(selectedPos.amount_to_pay ?? selectedPos.amount_due_now)} сум</strong>
											<small>Всего в месяц: {formatMoney(selectedPos.monthly_total)} сум</small>
											{Number(selectedPos.open_debt || 0) > 0 &&
												<small className="text-danger d-block">
													Открытый долг по начислениям: {formatMoney(selectedPos.open_debt)} сум
												</small>
											}
										</div>
										<button
											type="button"
											className="btn btn-primary subscription-activate-button"
											disabled={!selectedPos.can_activate}
											onClick={activatePos}>
											<i className="uil uil-bolt-alt" />
											Активировать
										</button>
									</div>
									{!selectedPos.can_activate &&
										<div className="subscription-insufficient">
											<i className="uil uil-exclamation-triangle" />
											Недостаточно средств. Переведите деньги с другого филиала, затем нажмите «Активировать».
										</div>
									}
								</div>
							</section>
						}
					</main>
				</div>
			}

			{activeTab === "charges" &&
				<section className="subscription-history-card card">
					<div className="card-body">
						<div className="subscription-history-heading">
							<h5>Начисления по периодам</h5>
							<p className="subscription-muted mb-0">
								{selectedPos
									? `${selectedPos.name}: тариф и услуги, adjustments и закрытие`
									: "Выберите филиал на вкладке «Тарифы»"}
							</p>
						</div>
						{!selectedPos || !(selectedPos.billing_charges || []).length
							? <div className="subscription-history-empty">Нет начислений typed-слоя (или SQL ещё не применён)</div>
							: <div className="table-responsive">
								<table className="table table-bordered mb-0 subscription-history-table">
									<thead>
										<tr>
											<th>Период</th>
											<th>Тип</th>
											<th>Статус</th>
											<th className="text-end">Сумма</th>
											<th className="text-end">Итого</th>
											<th className="text-end">Оплачено</th>
											<th className="text-end">База бонуса</th>
											<th>Бонус</th>
										</tr>
									</thead>
									<tbody>
										{(selectedPos.billing_charges || []).map(item => (
											<tr key={item.id}>
												<td className="text-nowrap">
													{formatDate(item.period_start, "dd.MM.yyyy")}
													{" — "}
													{formatDate(item.period_end, "dd.MM.yyyy")}
												</td>
												<td>
													{item.charge_kind_code === "service"
														? (item.service_name || "Услуга")
														: "Абонентка"}
												</td>
												<td>{chargeStatusLabel(item.status)}</td>
												<td className="text-end text-nowrap">{formatMoney(item.amount)}</td>
												<td className="text-end text-nowrap">{formatMoney(item.amount_final)}</td>
												<td className="text-end text-nowrap">{formatMoney(item.amount_allocated)}</td>
												<td className="text-end text-nowrap">{formatMoney(item.bonus_base_amount)}</td>
												<td>{item.is_bonus_accrued ? "Да" : "Нет"}</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						}
					</div>
				</section>
			}

			{activeTab === "transfer" &&
				<section className="subscription-transfer-card card">
					<div className="card-body">
						<div className="subscription-transfer-title">
							<span><i className="uil uil-exchange" /></span>
							<div>
								<h5>Перевод между филиалами</h5>
								<p>Перевод пополняет баланс получателя, но не активирует тариф автоматически.</p>
							</div>
						</div>
						<form className="subscription-transfer-form" onSubmit={transferBalance}>
							<div>
								<label>Откуда</label>
								<select
									className="form-select"
									value={transfer.from_pos_id}
									onChange={event => setTransfer({ ...transfer, from_pos_id: event.target.value })}>
									{data.map(item => (
										<option key={item.id} value={item.id}>
											{item.name} — {formatMoney(item.balance)} сум
										</option>
									))}
								</select>
							</div>
							<button
								type="button"
								className="subscription-swap"
								onClick={() => setTransfer({
									...transfer,
									from_pos_id: transfer.to_pos_id,
									to_pos_id: transfer.from_pos_id,
								})}>
								<i className="uil uil-exchange-alt" />
							</button>
							<div>
								<label>Куда</label>
								<select
									className="form-select"
									value={transfer.to_pos_id}
									onChange={event => setTransfer({ ...transfer, to_pos_id: event.target.value })}>
									{data.map(item => (
										<option key={item.id} value={item.id}>
											{item.name} — {formatMoney(item.balance)} сум
										</option>
									))}
								</select>
							</div>
							<div>
								<label>Сумма</label>
								<input
									type="number"
									className="form-control"
									placeholder="0"
									value={transfer.amount}
									onChange={event => setTransfer({ ...transfer, amount: event.target.value })}
								/>
							</div>
							<button
								type="submit"
								className="btn btn-dark subscription-transfer-button"
								disabled={
									data.length < 2 ||
									!transfer.amount ||
									Number(transfer.amount) <= 0 ||
									Number(transfer.from_pos_id) === Number(transfer.to_pos_id)
								}>
								Перевести
							</button>
						</form>
						<div className="subscription-transfer-note">
							<i className="uil uil-shield-check" />
							После перевода откройте вкладку «Тарифы и услуги», выберите филиал-получатель и нажмите «Активировать».
						</div>
					</div>
				</section>
			}

			{activeTab === "debts" &&
				renderHistoryTable(
					profilePos
						? `Пока нет списаний и погашений по филиалу «${profilePos.name}».`
						: "Пока нет списаний и погашений по текущему филиалу.",
					{
						hidePosColumn: true,
						title: profilePos
							? `${profilePos.name} — задолженности и погашения`
							: "Задолженности и погашения",
						subtitle: profilePos
							? `Филиал из профиля (ID ${profilePos.id}). Баланс: ${formatMoney(profilePos.balance)} сум. Показаны последние 100 операций.`
							: "Показаны последние 100 операций текущего филиала из профиля.",
					}
				)
			}

			{activeTab === "movements" &&
				renderHistoryTable("Пока нет движений по балансу филиалов.")
			}

			{activeTab === "telegram" && <TelegramGroupConnect title="Telegram-группа бренда" />}
		</div>
	)
}

export default SubscriptionAccount
